import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, InputAdornment, MenuItem, Avatar, Divider, Skeleton, alpha, Tabs, Tab,
  Switch, FormControlLabel, Tooltip, Paper, Alert
} from '@mui/material';
import {
  Add, Edit, Delete, Search, Business, People, Close, SupervisorAccount,
  Security, Visibility, VisibilityOff, PersonAdd, Badge, ArrowBack,
  AdminPanelSettings, ManageAccounts, Info, ContentCopy
} from '@mui/icons-material';
import { usersAPI, companiesAPI, rolesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// Reusable permission section component
const PermissionSection = ({ title, color, perms, togglePerm, fields }) => (
  <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2.5, borderColor: alpha(color, 0.2) }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      {title}
    </Typography>
    <Grid container spacing={1}>
      {fields.map(f => (
        <Grid item xs={6} sm={4} md={3} key={f.key}>
          <FormControlLabel
            control={<Switch size="small" checked={!!perms[f.key]} onChange={() => togglePerm(f.key)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: color } }} />}
            label={<Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{f.label}</Typography>}
          />
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Super Admin management
  const [superAdmins, setSuperAdmins] = useState([]);
  const [saOpen, setSaOpen] = useState(false);
  const [saForm, setSaForm] = useState({ firstName: '', lastName: '', username: '', email: '', password: '' });

  // Company / Tenant management
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);

  // Create company
  const [companyOpen, setCompanyOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', email: '', phone: '' });

  // Create user for a company
  const [cuOpen, setCuOpen] = useState(false);
  const [cuForm, setCuForm] = useState({ firstName: '', lastName: '', username: '', email: '', password: '', role: 'viewer' });

  // User permissions editor
  const [permOpen, setPermOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permData, setPermData] = useState({});

  // User role editor
  const [roleSelectUser, setRoleSelectUser] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [saRes, compRes] = await Promise.all([usersAPI.getSuperAdmins(), usersAPI.getAllCompanies()]);
      setSuperAdmins(Array.isArray(saRes.data) ? saRes.data : saRes.data?.data || []);
      setCompanies(Array.isArray(compRes.data) ? compRes.data : compRes.data?.data || []);
    } catch (e) { console.error(e); toast.error('Failed to load admin data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Admin | MyPayPlus'; }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const loadCompanyUsers = async (companyId) => {
    setCompanyUsersLoading(true);
    try {
      const res = await usersAPI.getCompanyUsers(companyId);
      setCompanyUsers(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (e) { toast.error('Failed to load users'); }
    finally { setCompanyUsersLoading(false); }
  };

  const handleCreateSuperAdmin = async () => {
    try {
      await usersAPI.createSuperAdmin(saForm);
      toast.success('Super admin created');
      setSaOpen(false);
      setSaForm({ firstName: '', lastName: '', username: '', email: '', password: '' });
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create super admin'); }
  };

  const handleCreateCompany = async () => {
    try {
      await companiesAPI.create(companyForm);
      toast.success('Company created');
      setCompanyOpen(false);
      setCompanyForm({ name: '', email: '', phone: '' });
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create company'); }
  };

  const handleCreateCompanyUser = async () => {
    try {
      await usersAPI.create({ ...cuForm, company: selectedCompany._id });
      toast.success('User created');
      setCuOpen(false);
      setCuForm({ firstName: '', lastName: '', username: '', email: '', password: '', role: 'viewer' });
      loadCompanyUsers(selectedCompany._id);
      loadData(); // refresh counts
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create user'); }
  };

  const openPermEditor = (u) => {
    setPermUser(u);
    setPermData(u.permissions || getDefaultPermissions(u.role));
    setPermOpen(true);
  };

  const getDefaultPermissions = (role) => ({
    collections: { canAdd: true, canEdit: role !== 'viewer', canDelete: role === 'admin', canViewBillingRate: role !== 'collector', canViewInvoiceAmount: role !== 'collector', canViewProfit: role === 'admin' || role === 'manager', canViewCommission: role !== 'viewer', canViewProviderSection: true, canViewContractorSection: true },
    providers: { canAdd: role !== 'viewer', canEdit: role !== 'viewer', canDelete: role === 'admin', canView: true },
    contractors: { canAdd: role !== 'viewer', canEdit: role !== 'viewer', canDelete: role === 'admin', canView: true },
    billing: { canView: role !== 'collector', canManage: role === 'admin' || role === 'billing_specialist' },
    credentialing: { canView: true, canManage: role === 'admin' },
    reports: { canView: role !== 'collector' },
    settings: { canView: role === 'admin', canManage: role === 'admin' },
  });

  const handleSavePermissions = async () => {
    try {
      await usersAPI.updatePermissions(permUser._id, { permissions: permData });
      toast.success('Permissions updated');
      setPermOpen(false);
      if (selectedCompany) loadCompanyUsers(selectedCompany._id);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to update permissions'); }
  };

  const togglePerm = (section, key) => {
    setPermData(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: !prev[section]?.[key] }
    }));
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      await usersAPI.update(userId, { role: newRole });
      toast.success('Role updated');
      if (selectedCompany) loadCompanyUsers(selectedCompany._id);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to update role'); }
  };

  const roleColor = (role) => {
    const map = { super_admin: '#dc2626', admin: '#3b82f6', manager: '#8b5cf6', billing_specialist: '#f59e0b', collector: '#10b981', viewer: '#94a3b8' };
    return map[role] || '#64748b';
  };

  const filteredCompanies = companies.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Box sx={{ p: 3 }}>{[...Array(4)].map((_, i) => <Skeleton key={i} height={80} sx={{ mb: 1 }} />)}</Box>;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ width: 48, height: 48, background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
          <SupervisorAccount sx={{ fontSize: 28 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Tenant Manager</Typography>
          <Typography variant="body2" color="text.secondary">Manage companies, super admins, and user permissions across all tenants</Typography>
        </Box>
      </Box>

      {/* How It Works Guide */}
      <Alert severity="info" icon={<Info />} sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', mb: 0.5 }}>How Multi-Tenant Management Works</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          <strong>Super Admins</strong> have platform-level access and can manage all companies.&nbsp;
          <strong>Companies</strong> are separate tenants — each with its own users, data, and settings.&nbsp;
          Click a company to view its users, change roles, or edit permissions. Use the <strong>Super Admins</strong> tab to add platform administrators.
        </Typography>
      </Alert>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Companies', value: companies.length, icon: <Business />, color: '#3b82f6', desc: 'Total tenants' },
          { label: 'Super Admins', value: superAdmins.length, icon: <AdminPanelSettings />, color: '#dc2626', desc: 'Platform admins' },
          { label: 'Total Users', value: companies.reduce((a, c) => a + (c.userCount || 0), 0), icon: <People />, color: '#8b5cf6', desc: 'Across all tenants' },
        ].map((stat, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: alpha(stat.color, 0.15), cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: stat.color, transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${alpha(stat.color, 0.15)}` } }}
              onClick={() => setTab(i === 1 ? 1 : 0)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color, width: 48, height: 48 }}>
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{stat.label}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{stat.desc}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelectedCompany(null); }}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
          <Tab label="Companies" icon={<Business sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }} />
          <Tab label="Super Admins" icon={<AdminPanelSettings sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }} />
        </Tabs>

        <CardContent>
          {/* Tab 0 – Companies */}
          {tab === 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search companies..." value={search}
                  onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                  sx={{ flex: 1, maxWidth: 320 }} />
                <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setCompanyOpen(true)}
                  sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700 }}>
                  New Company
                </Button>
              </Box>

              {selectedCompany ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<ArrowBack />} onClick={() => setSelectedCompany(null)} sx={{ fontWeight: 600 }}>
                      Back to Companies
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Avatar sx={{ width: 32, height: 32, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }}>
                      <Business sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>{selectedCompany.name}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{selectedCompany.email || 'No email'}</Typography>
                    </Box>
                    <Chip label={`${companyUsers.length} users`} size="small" sx={{ fontWeight: 600, ml: 1 }} />
                    <Box sx={{ ml: 'auto' }}>
                      <Button size="small" variant="contained" startIcon={<PersonAdd />}
                        onClick={() => setCuOpen(true)}
                        sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700, fontSize: '0.78rem' }}>
                        Add User
                      </Button>
                    </Box>
                  </Box>

                  {companyUsersLoading ? (
                    <Box>{[...Array(3)].map((_, i) => <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />)}</Box>
                  ) : companyUsers.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
                      <People sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                      <Typography sx={{ fontWeight: 600, color: '#94a3b8', mb: 1 }}>No users in this company</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1', mb: 2 }}>
                        Create the first user for this company to get started.
                      </Typography>
                      <Button size="small" variant="contained" startIcon={<PersonAdd />}
                        onClick={() => setCuOpen(true)}
                        sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700 }}>
                        Add User
                      </Button>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {companyUsers.map(u => (
                            <TableRow key={u._id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: roleColor(u.role) }}>
                                    {u.firstName?.[0]}{u.lastName?.[0]}
                                  </Avatar>
                                  <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{u.firstName} {u.lastName}</Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{u.email || ''}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.82rem', color: '#64748b' }}>{u.username}</TableCell>
                              <TableCell>
                                <TextField select size="small" value={u.role || 'viewer'}
                                  onChange={(e) => handleChangeUserRole(u._id, e.target.value)}
                                  sx={{ minWidth: 140, '& .MuiInputBase-root': { fontSize: '0.78rem', height: 32 } }}>
                                  {['admin', 'manager', 'billing_specialist', 'collector', 'viewer'].map(r => (
                                    <MenuItem key={r} value={r} sx={{ fontSize: '0.82rem' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: roleColor(r) }} />
                                        <span style={{ textTransform: 'capitalize' }}>{r.replace(/_/g, ' ')}</span>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <Chip label={u.isActive !== false ? 'Active' : 'Inactive'} size="small"
                                  sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: u.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: u.isActive !== false ? '#059669' : '#dc2626' }} />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit Permissions">
                                  <IconButton size="small" onClick={() => openPermEditor(u)} sx={{ color: '#3b82f6' }}>
                                    <Security sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              ) : (
                <>
                  {filteredCompanies.length === 0 && !search ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
                      <Business sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                      <Typography sx={{ fontWeight: 600, color: '#94a3b8', mb: 1 }}>No companies yet</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1', mb: 2 }}>
                        Create your first company (tenant) to get started with multi-tenant management.
                      </Typography>
                      <Button variant="contained" startIcon={<Add />} onClick={() => setCompanyOpen(true)}
                        sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700 }}>Create Company</Button>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell align="center">Users</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredCompanies.map(c => (
                            <TableRow key={c._id} hover sx={{ cursor: 'pointer' }} onClick={() => { setSelectedCompany(c); loadCompanyUsers(c._id); }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ width: 34, height: 34, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }}>
                                    <Business sx={{ fontSize: 18 }} />
                                  </Avatar>
                                  <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>{c.name}</Typography>
                                    {c.email && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{c.email}</Typography>}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={`${c.userCount || 0} users`} size="small" sx={{ fontWeight: 600, bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }} />
                              </TableCell>
                              <TableCell align="right">
                                <Button size="small" startIcon={<ManageAccounts sx={{ fontSize: 16 }} />}
                                  onClick={(e) => { e.stopPropagation(); setSelectedCompany(c); loadCompanyUsers(c._id); }}
                                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                  Manage Users
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {filteredCompanies.length === 0 && search && (
                            <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>No companies match "{search}"</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </>
          )}

          {/* Tab 1 – Super Admins */}
          {tab === 1 && (
            <>
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography sx={{ fontSize: '0.8rem' }}>
                  <strong>Super Admins</strong> have unrestricted access to ALL companies and data on the platform. Only grant this role to trusted platform administrators.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" size="small" startIcon={<PersonAdd />} onClick={() => setSaOpen(true)}
                  sx={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', fontWeight: 700 }}>
                  Add Super Admin
                </Button>
              </Box>

              {superAdmins.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
                  <AdminPanelSettings sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#94a3b8', mb: 1 }}>No super admins found</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1', mb: 2 }}>
                    Create a super admin to manage your multi-tenant platform.
                  </Typography>
                  <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setSaOpen(true)}
                    sx={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', fontWeight: 700 }}>Add Super Admin</Button>
                </Paper>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {superAdmins.map(sa => (
                        <TableRow key={sa._id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: 12, background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                                {sa.firstName?.[0]}{sa.lastName?.[0]}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>{sa.firstName} {sa.lastName}</Typography>
                                {sa.company?.name && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>Company: {sa.company.name}</Typography>}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#64748b' }}>{sa.username}</TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#64748b' }}>{sa.email}</TableCell>
                          <TableCell>
                            <Chip label="Super Admin" size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: alpha('#dc2626', 0.1), color: '#dc2626' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Super Admin Dialog */}
      <Dialog open={saOpen} onClose={() => setSaOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #dc2626, #f97316)', borderRadius: 2 }}>
            <AdminPanelSettings sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Add Super Admin</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Platform-level administrator with access to all companies</Typography>
          </Box>
          <IconButton onClick={() => setSaOpen(false)} sx={{ ml: 'auto' }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: '0.78rem' }}>
            This user will be able to manage ALL companies, users, and settings across the entire platform.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="First Name *" value={saForm.firstName} onChange={e => setSaForm(f => ({ ...f, firstName: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Last Name *" value={saForm.lastName} onChange={e => setSaForm(f => ({ ...f, lastName: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Username *" value={saForm.username} onChange={e => setSaForm(f => ({ ...f, username: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email *" value={saForm.email} onChange={e => setSaForm(f => ({ ...f, email: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Password *" type="password" value={saForm.password} onChange={e => setSaForm(f => ({ ...f, password: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSaOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSuperAdmin}
            disabled={!saForm.firstName || !saForm.lastName || !saForm.username || !saForm.email || !saForm.password}
            sx={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', fontWeight: 700 }}>
            Create Super Admin
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Company Dialog */}
      <Dialog open={companyOpen} onClose={() => setCompanyOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', borderRadius: 2 }}>
            <Business sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Create New Company</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Add a new tenant to the platform</Typography>
          </Box>
          <IconButton onClick={() => setCompanyOpen(false)} sx={{ ml: 'auto' }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Company Name *" value={companyForm.name} onChange={e => setCompanyForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email" value={companyForm.email} onChange={e => setCompanyForm(f => ({ ...f, email: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Phone" value={companyForm.phone} onChange={e => setCompanyForm(f => ({ ...f, phone: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCompanyOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCompany}
            disabled={!companyForm.name}
            sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700 }}>
            Create Company
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User for Company Dialog */}
      <Dialog open={cuOpen} onClose={() => setCuOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: 2 }}>
            <PersonAdd sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Add User to {selectedCompany?.name}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Create a new user for this company</Typography>
          </Box>
          <IconButton onClick={() => setCuOpen(false)} sx={{ ml: 'auto' }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth size="small" label="First Name *" value={cuForm.firstName} onChange={e => setCuForm(f => ({ ...f, firstName: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Last Name *" value={cuForm.lastName} onChange={e => setCuForm(f => ({ ...f, lastName: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Username *" value={cuForm.username} onChange={e => setCuForm(f => ({ ...f, username: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email *" value={cuForm.email} onChange={e => setCuForm(f => ({ ...f, email: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Password *" type="password" value={cuForm.password} onChange={e => setCuForm(f => ({ ...f, password: e.target.value }))} /></Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Role" select value={cuForm.role} onChange={e => setCuForm(f => ({ ...f, role: e.target.value }))}>
                {['admin', 'manager', 'billing_specialist', 'collector', 'viewer'].map(r => (
                  <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCuOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCompanyUser}
            disabled={!cuForm.firstName || !cuForm.lastName || !cuForm.username || !cuForm.email || !cuForm.password}
            sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', fontWeight: 700 }}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Editor Dialog */}
      <Dialog open={permOpen} onClose={() => setPermOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: alpha('#3b82f6', 0.15), color: '#3b82f6', borderRadius: 2 }}>
            <Security sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Edit Permissions — {permUser?.firstName} {permUser?.lastName}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Role: {(permUser?.role || '').replace(/_/g, ' ')} | Company: {selectedCompany?.name || 'N/A'}</Typography>
          </Box>
          <IconButton onClick={() => setPermOpen(false)} sx={{ ml: 'auto' }} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {/* Collections */}
          <PermissionSection title="Collections" color="#f59e0b" perms={permData.collections || {}} togglePerm={(k) => togglePerm('collections', k)}
            fields={[
              { key: 'canAdd', label: 'Can Add' }, { key: 'canEdit', label: 'Can Edit' }, { key: 'canDelete', label: 'Can Delete' },
              { key: 'canViewBillingRate', label: 'View Billing Rate' }, { key: 'canViewInvoiceAmount', label: 'View Invoice Amount' },
              { key: 'canViewProfit', label: 'View Profit' }, { key: 'canViewCommission', label: 'View Commission' },
              { key: 'canViewProviderSection', label: 'View Provider Info' }, { key: 'canViewContractorSection', label: 'View Contractor Info' },
            ]} />
          <PermissionSection title="Providers" color="#3b82f6" perms={permData.providers || {}} togglePerm={(k) => togglePerm('providers', k)}
            fields={[{ key: 'canView', label: 'Can View' }, { key: 'canAdd', label: 'Can Add' }, { key: 'canEdit', label: 'Can Edit' }, { key: 'canDelete', label: 'Can Delete' }]} />
          <PermissionSection title="Contractors" color="#8b5cf6" perms={permData.contractors || {}} togglePerm={(k) => togglePerm('contractors', k)}
            fields={[{ key: 'canView', label: 'Can View' }, { key: 'canAdd', label: 'Can Add' }, { key: 'canEdit', label: 'Can Edit' }, { key: 'canDelete', label: 'Can Delete' }]} />
          <PermissionSection title="Billing" color="#10b981" perms={permData.billing || {}} togglePerm={(k) => togglePerm('billing', k)}
            fields={[{ key: 'canView', label: 'Can View' }, { key: 'canManage', label: 'Can Manage' }]} />
          <PermissionSection title="Credentialing" color="#06b6d4" perms={permData.credentialing || {}} togglePerm={(k) => togglePerm('credentialing', k)}
            fields={[{ key: 'canView', label: 'Can View' }, { key: 'canManage', label: 'Can Manage' }]} />
          <PermissionSection title="Reports" color="#f97316" perms={permData.reports || {}} togglePerm={(k) => togglePerm('reports', k)}
            fields={[{ key: 'canView', label: 'Can View' }]} />
          <PermissionSection title="Settings" color="#6b7280" perms={permData.settings || {}} togglePerm={(k) => togglePerm('settings', k)}
            fields={[{ key: 'canView', label: 'Can View' }, { key: 'canManage', label: 'Can Manage' }]} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPermOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePermissions}
            sx={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', fontWeight: 700 }}>
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
